import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    requestDeleteClientVariable,
    requestClientVariables,
    requestClientVariableDataTypes,
    requestClientVariableGroupings,
    updateDestinationOrder,
} from "../../../actions/clientVariableActions";
import { showModal } from "../../../actions/actionCreators";
import ClientVariableAdder from "./ClientVariableAdder";

import IconSwitch from "../../material-components/Misc/IconSwitch";
import { getArrayOfClientVariables } from "../../../reducers/clientVariables";
import { stableSort, getSorting } from "../../../helpers/materialTableFunctions";

// custom material ui table components
import TableHeader from "../../material-components/Table/TableHeader";
import TableBody from "../../material-components/Table/TableBody";
import TableFooter from "../../material-components/Table/TableFooter";

// importing material-ui pl

import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import { IAppState } from "../../../types/stores/index";
import { IClientVariable } from "../../../types/stores/vars";
import { TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";

interface IVariableMain {
    scope: string;
}

const VariableMain: React.FC<IVariableMain> = ({ scope }: IVariableMain) => {
    const dispatch = useDispatch();

    const [order, setOrder] = useState("desc");
    const [orderBy, setOrderBy] = useState("VariableName");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [manageDestVar, setManageDestVar] = useState(false);
    const [filter, setFilter] = useState("");

    //Props
    const clientVariables = useSelector((state: IAppState) => getArrayOfClientVariables(state));
    const isShowDestination = useSelector((state: IAppState) =>
        scope == "OfferCode" || scope == "CampaignFlow" ? state.session.companyIsDestination : false
    );
    const loadingListDestinationOrder = useSelector((state: IAppState) => state.vars.loadingListDestinationOrder);
    const variablesById = useSelector((state: IAppState) => state.clientVariables.byId);
    const entityName = scope == "OfferCode" ? "offer code" : "variable";
    const roles = useSelector((state: IAppState) => state.session.roles || []);
    const enabledFeatures = useSelector((state: IAppState) => state.session.enabledFeatures);

    //Roles
    const hasSuperAdmin = roles.includes("Super Admin");
    const hasExperianAdmin = roles.includes("Experian Admin");

    //Features
    const hasClientVariablesManagement = enabledFeatures.includes("client-variables-management");
    const hasMultiExport = enabledFeatures.includes("flow-tool-multi-export");

    useEffect(() => {
        dispatch(requestClientVariables());
        dispatch(requestClientVariableDataTypes());
        dispatch(requestClientVariableGroupings());
    }, [dispatch]);

    const deleteVariable = id => dispatch(requestDeleteClientVariable(id));

    const renderNewYesNoColumn = value => <span>{value.startsWith("Destination") ? "Yes" : "No"}</span>;

    const handleChangeManageDestVar = val => setManageDestVar(val);

    const renderNewActionColumn = value => (
        <Tooltip title="Delete">
            <IconButton id="btn-delete" color="secondary" onClick={() => renderConfirmDelete(value)} size="small">
                <i className={"material-icons"} style={{ height: "16px", width: "16px", fontSize: "16px" }}>
                    delete
                </i>
            </IconButton>
        </Tooltip>
    );

    const renderConfirmDelete = value => {
        const modalProps = {
            entityName,
            variable: variablesById[value],
            handleDelete: () => deleteVariable(value),
            scope,
        };
        dispatch(showModal("VARIABLE_ADMIN_CONFIRM_DELETE", modalProps));
    };

    const filterVariablesByScope = (): Array<IClientVariable> => {
        const destinationScope = scope == "OfferCode" ? "DestinationOffer" : "DestinationVariable";
        let scopeVariables: Array<IClientVariable> = [];
        if (isShowDestination) {
            scopeVariables = clientVariables.filter(
                x => x.VariableScope == scope || x.VariableScope == destinationScope
            );
        } else {
            scopeVariables = clientVariables.filter(x => x.VariableScope == scope);
        }

        return scopeVariables;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const tableDataFilteredByScope = useMemo(() => filterVariablesByScope(), [clientVariables, scope]);

    // Table data taken is based on the previous filter by Scope
    const filterVariablesByName = (): Array<IClientVariable> => {
        if (filter.length > 0) {
            setPage(0);
            return tableDataFilteredByScope.filter(x => x.VariableName.toLowerCase().includes(filter.toLowerCase()));
        }

        return tableDataFilteredByScope;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const tableDataFilteredByName = useMemo(() => filterVariablesByName(), [filter, tableDataFilteredByScope]);

    const handleRequestSort = (_, property: string) => {
        setOrder(order === "asc" ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangeDestinationOrder = (event, destinationOrder) => {
        if (event != null) {
            let current = clientVariables.filter(x => x.DestinationOrder == destinationOrder);
            let previousPosition = clientVariables.filter(x => x.DestinationOrder == event.target.value);
            dispatch(
                updateDestinationOrder(destinationOrder, current[0].Id, event.target.value, previousPosition[0].Id)
            );
        }
    };

    const renderOrderList = (destinationOrder: number) => {
        const options = clientVariables
            .filter(x => x.DestinationOrder != 0)
            .map((field, i) => (
                <MenuItem key={i} value={field.DestinationOrder}>
                    {field.DestinationOrder}
                </MenuItem>
            ));
        return (
            <Select
                style={{ fontSize: "14px" }}
                value={destinationOrder}
                onChange={e => {
                    handleChangeDestinationOrder(e, destinationOrder);
                }}
                displayEmpty
                disabled={loadingListDestinationOrder}
            >
                {options}
            </Select>
        );
    };

    const getData = () => {
        const data: Array<Array<Array<string | number | JSX.Element | null> | boolean>> = [];
        const sortedData: Array<IClientVariable> = stableSort(tableDataFilteredByName, getSorting(order, orderBy));

        sortedData.map(dataRow => {
            if (manageDestVar) {
                if (dataRow.VariableScope == "DestinationVariable") {
                    data.push([
                        ["VariableName", dataRow.VariableName, "smDown"],
                        [
                            "VariableKind",
                            dataRow.VariableSubType != null ? dataRow.VariableSubType : dataRow.VariableKind,
                            "smDown",
                        ],
                        isShowDestination && ["VariableScope", renderNewYesNoColumn(dataRow.VariableScope), "mdDown"],
                        ["DestinationOrder", renderOrderList(dataRow.DestinationOrder), "mdDown"],
                        hasMultiExport && ["GroupName", dataRow.GroupName, "smDown"],
                        ["Actions", renderNewActionColumn(dataRow.Id), "smDown"],
                    ]);
                }
            } else {
                data.push([
                    ["VariableName", dataRow.VariableName, "smDown"],
                    [
                        "VariableKind",
                        dataRow.VariableSubType != null ? dataRow.VariableSubType : dataRow.VariableKind,
                        "smDown",
                    ],
                    isShowDestination && ["VariableScope", renderNewYesNoColumn(dataRow.VariableScope), "mdDown"],
                    hasMultiExport && ["GroupName", dataRow.GroupName, "smDown"],
                    ["Actions", renderNewActionColumn(dataRow.Id), "smDown"],
                ]);
            }
        });

        return data;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const data = useMemo(() => getData(), [tableDataFilteredByName, order, orderBy, page, rowsPerPage, filter]);

    return scope != "CampaignFlow" || hasSuperAdmin || hasExperianAdmin || hasClientVariablesManagement ? (
        <div>
            <div className="row">
                <div className="col-sm-12 col-md-6">
                    <h4>
                        Current {manageDestVar ? "Destination " : ""}
                        {entityName
                            .split(" ")
                            .map(s => s.charAt(0).toUpperCase() + s.substring(1))
                            .join(" ")}
                        s
                    </h4>
                </div>
                <div className="col-sm-12 col-md-6">
                    <Autocomplete
                        id="table-search"
                        autoComplete
                        options={tableDataFilteredByScope}
                        onChange={(_, value) => setFilter(value?.VariableName ?? "")}
                        getOptionLabel={option => option.VariableName}
                        getOptionSelected={(option, value) => value.Id === option.Id}
                        renderOption={option => (
                            <React.Fragment>
                                {option.VariableName} -{" "}
                                {option.VariableSubType != null ? option.VariableSubType : option.VariableKind}
                            </React.Fragment>
                        )}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="Search"
                                variant="standard"
                                helperText={"Type here to search for name"}
                                fullWidth
                            />
                        )}
                    />
                </div>
            </div>
            {
                <>
                    {isShowDestination && entityName === "variable" && (
                        <>
                            <label className="control-label" htmlFor="">
                                Manage Destination Variables
                            </label>
                            <div>
                                <IconSwitch
                                    id="manage-dest-var"
                                    name="manageDestVar"
                                    onChange={e => handleChangeManageDestVar(e.target.checked)}
                                    checked={manageDestVar}
                                />
                            </div>
                        </>
                    )}
                    <Paper>
                        <Table size="medium">
                            <TableHeader
                                headers={[
                                    ["VariableName", "Name", "smDown"],
                                    ["VariableKind", "Type", "smDown"],
                                    isShowDestination && ["VariableScope", "Destination", "mdDown"],
                                    manageDestVar && ["DestinationOrder", "Order", "mdDown"],
                                    hasMultiExport && ["GroupName", "Grouping", "smDown"],
                                    ["Actions", "Actions", "smDown"],
                                ]}
                                orderBy={orderBy}
                                order={order}
                                onClick={handleRequestSort}
                            />
                            <TableBody
                                data={data}
                                page={page}
                                rowsPerPage={rowsPerPage}
                                emptyMessage={["EmptyMessage", "No Data to Display.", "smDown"]}
                            />
                        </Table>
                        <TableFooter
                            displayDense={false}
                            displayPaging={true}
                            rowsPerPageOptions={[5, 10, 15]}
                            count={tableDataFilteredByName.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            onRowsPerPageChange={e => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                        />
                    </Paper>{" "}
                </>
            }
            <h4>
                Add New{" "}
                {entityName
                    .split(" ")
                    .map(s => s.charAt(0).toUpperCase() + s.substring(1))
                    .join(" ")}
            </h4>
            <Paper style={{ padding: "0px 10px 10px 10px" }}>
                <ClientVariableAdder
                    scope={scope}
                    isShowDestination={isShowDestination}
                    manageDestVar={manageDestVar}
                />
            </Paper>
        </div>
    ) : null;
};

export default VariableMain;
